import React, { useContext, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  useTheme,
  IconButton,
  Paper,
  TextField,
  Chip,
  Collapse,
  MenuItem,
  FormControl,
  Menu,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { FormInputText } from "../forms/FormInputText";
import { useCreateProperty } from "../../hooks/query/useProperty";
import * as toast from "../../utils/alertMessage";
import { AuthContext } from "../../context/authContext";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import HomeIcon from "@mui/icons-material/Home";
import SaveIcon from "@mui/icons-material/Save";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import GooglePlacesAutocomplete, {
  geocodeByPlaceId,
} from "react-google-places-autocomplete";
import { parseISO } from "date-fns";
import { useGetAllProblems } from "../../hooks/query/useProblem";
import { useGetAllScope } from "../../hooks/query/useOccupation";
import FallbackLoader from "../loading/FallBackLoader";
import { ExpandLess as ExpandLessIcon } from "@mui/icons-material";
import { ExpandMoreOutlined as ExpandMoreIcon } from "@mui/icons-material";
import NestedMenuComponents from "../nestedmenu/NestedMenuItem";
const schema = z.object({
  address: z.string().optional(),
  dot: z.string().nullable().optional(),
  dob: z.string().nullable().optional(),
  scopeOccupation: z
    .string()
    .min(1, { message: "Please select an occupation" }),
  birthPlace: z.string().optional(),
  houseHeadName: z
    .string()
    .min(1, { message: "Please enter the house head name" }),
  problemFacing: z
    .array(z.string())
    .min(1, { message: "Please select at least one problem" }),
});

interface CreatePropertyDialogProps {
  open: boolean;
  onClose: () => void;
}

interface Coordinates {
  lat: number | null;
  lng: number | null;
}

const CreatePropertyDialog: React.FC<CreatePropertyDialogProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();
  const { currentUser } = useContext(AuthContext);
  const [coordinates, setCoordinates] = useState<Coordinates>({
    lat: null,
    lng: null,
  });
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  // const navigate = useNavigate();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedOccupation, setSelectedOccupation] = useState("");
  const [isBirthDetailsExpanded, setIsBirthDetailsExpanded] = useState(false);
  const [useCurrentLocation, setUseCurrentLocation] = useState(false);

  const toggleBirthDetails = () => {
    setIsBirthDetailsExpanded((prev) => !prev);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const { data: problems, isLoading: isProblemLoading } = useGetAllProblems();
  const { data: occupations, isLoading: isOccupationLoading } =
    useGetAllScope();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      address: "",
      dob: null,
      dot: "",
      scopeOccupation: "",
      birthPlace: "",
      houseHeadName: "",
      problemFacing: [],
    },
  });

  const getCurrentLocation = () => {
    setIsLoadingLocation(true);
    setLocationError(null);
    setUseCurrentLocation(true);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setCoordinates({ lat: latitude, lng: longitude });

          fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=AIzaSyDyo2gkJd1Gb31woHIeK51VSmvTIJXxfKo`
          )
            .then((response) => response.json())
            .then((data) => {
              if (data.results && data.results[0]) {
                setValue("address", data.results[0].formatted_address);
              }
              setIsLoadingLocation(false);
            })
            .catch((error) => {
              console.error("Error fetching address:", error);
              setIsLoadingLocation(false);
            });
        },
        (error) => {
          setLocationError(
            "Error getting location. Please try again or enter address manually."
          );
          setIsLoadingLocation(false);
          setUseCurrentLocation(false);
          console.error("Error getting location:", error);
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0,
        }
      );
    } else {
      setLocationError("Geolocation is not supported by your browser");
      setIsLoadingLocation(false);
      setUseCurrentLocation(false);
    }
  };

  const fetchCoordinates = async (placeId: string) => {
    try {
      const results = await geocodeByPlaceId(placeId);
      const { lat, lng } = results[0].geometry.location;
      setCoordinates({
        lat: typeof lat === "function" ? lat() : lat ?? null,
        lng: typeof lng === "function" ? lng() : lng ?? null,
      });
      setUseCurrentLocation(false);
    } catch (error) {
      console.error("Error fetching coordinates:", error);
    }
  };

  const { mutate: createProperty, isLoading } = useCreateProperty();
  const navigate = useNavigate();

  const submitForm = (data: any) => {
    const requestBody: any = {
      ...data,
      scopeOccupation: { id: data.scopeOccupation },
      latitude: coordinates.lat,
      longitude: coordinates.lng,
    };

    if (data.dob) {
      requestBody.dob = new Date(data.dob).toISOString().split("T")[0];
    } else {
      delete requestBody.dob;
    }

    createProperty(
      {
        requestBody,
        tenantId: currentUser.tenant.id,
      },
      {
        onSuccess: (response) => {
          reset();
          onClose();
          toast.success("Property created successfully");
          // Directly navigate to the map
          navigate(`/home/${response?.propertyId}`, {
            state: {
              latitude: coordinates.lat,
              longitude: coordinates.lng,
            },
          });
        },
        onError: (error: any) => {
          console.error("Error creating property:", error);
          toast.error(error?.body?.error?.message);
        },
      }
    );
  };

  if (isOccupationLoading || isProblemLoading) {
    return <FallbackLoader />;
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: theme.spacing(4),
            boxShadow: "0 15px 35px rgba(187,0,0,0.1)",
            background: "linear-gradient(145deg, #ffffff, #f0f0f0)",
            border: "1px solid rgba(187,0,0,0.1)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 3,
            pb: 0,
            background: `linear-gradient(90deg, ${theme.palette.primary.main}15, transparent)`,
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <HomeIcon
              sx={{
                color: "#bb0000",
                fontSize: "2.5rem",
                background: "rgba(187,0,0,0.1)",
                borderRadius: "50%",
                p: 1,
              }}
            />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#bb0000",
                letterSpacing: "-0.5px",
              }}
            >
              Create New Property
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: "#bb0000",
              background: "rgba(187,0,0,0.05)",
              "&:hover": {
                background: "rgba(187,0,0,0.1)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ pt: 2, pb: 0 }}>
          <Paper
            elevation={0}
            sx={{
              background: "transparent",
              p: { xs: 1, sm: 3 },
              borderRadius: theme.spacing(3),
              mb: 2,
            }}
          >
            <form onSubmit={handleSubmit(submitForm)}>
              <Grid container spacing={{ xs: 2, sm: 3 }}>
                <Grid item xs={12}>
                  <FormInputText
                    name="houseHeadName"
                    control={control}
                    label="House Head Name"
                    placeholder="Enter house head name"
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <Controller
                      name="scopeOccupation"
                      control={control}
                      render={({ field }) => (
                        <>
                          <TextField
                            label="Occupation"
                            value={selectedOccupation}
                            placeholder="Select Occupation"
                            onClick={handleMenuOpen}
                            error={!!errors.scopeOccupation}
                            helperText={errors.scopeOccupation?.message}
                            InputProps={{
                              readOnly: true,
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: "rgba(187,0,0,0.3)",
                                },
                                "&:hover fieldset": {
                                  borderColor: "#bb0000",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#bb0000",
                                },
                              },
                            }}
                          />

                          <Menu
                            anchorEl={menuAnchorEl}
                            open={Boolean(menuAnchorEl)}
                            onClose={handleMenuClose}
                          >
                            <NestedMenuComponents.Container
                              parentMenuOpen={Boolean(menuAnchorEl)}
                            >
                              {occupations?.map((occupation) => (
                                <MenuItem
                                  key={occupation.id}
                                  onClick={() => {
                                    field.onChange(occupation.id);
                                    setSelectedOccupation(
                                      occupation.displayName ?? ""
                                    );
                                    handleMenuClose();
                                  }}
                                >
                                  {occupation.displayName}
                                </MenuItem>
                              ))}
                            </NestedMenuComponents.Container>
                          </Menu>
                        </>
                      )}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="problemFacing"
                    control={control}
                    defaultValue={[]}
                    render={({ field }) => (
                      <Box>
                        <TextField
                          select
                          fullWidth
                          label="Problem Facing"
                          value=""
                          onChange={(e) => {
                            const selectedProblemId = e.target.value as string;
                            if (selectedProblemId) {
                              const selectedProblem = problems?.find(
                                (p) => p.id === selectedProblemId
                              );
                              if (selectedProblem) {
                                field.onChange([
                                  ...(field.value || []),
                                  selectedProblemId,
                                ]);
                              }
                            }
                          }}
                          error={!!errors.problemFacing}
                          helperText={errors.problemFacing?.message}
                          SelectProps={{
                            native: false,
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "rgba(187,0,0,0.3)",
                              },
                              "&:hover fieldset": {
                                borderColor: "#bb0000",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#bb0000",
                              },
                            },
                          }}
                        >
                          {problems?.map((problem) => (
                            <MenuItem key={problem.id} value={problem.id}>
                              {problem.displayName}
                            </MenuItem>
                          ))}
                        </TextField>

                        {field.value && field.value.length > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1,
                              mt: 1,
                            }}
                          >
                            {field.value.map((problemId) => {
                              const problem = problems?.find(
                                (p) => p.id === problemId
                              );
                              return (
                                <Chip
                                  key={problemId}
                                  label={problem?.displayName}
                                  onDelete={() => {
                                    const updatedValues = field.value.filter(
                                      (id) => id !== problemId
                                    );
                                    field.onChange(updatedValues);
                                  }}
                                  sx={{
                                    backgroundColor:
                                      theme.palette.primary.light,
                                    color: theme.palette.primary.contrastText,
                                  }}
                                />
                              );
                            })}
                          </Box>
                        )}
                      </Box>
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ position: "relative" }}>
                    {!useCurrentLocation && (
                      <Controller
                        name="address"
                        control={control}
                        defaultValue=""
                        render={({ field, fieldState }) => (
                          <div style={{ width: "100%" }}>
                            <GooglePlacesAutocomplete
                              apiKey={import.meta.env.VITE_API_KEY}
                              selectProps={{
                                value: field.value
                                  ? { label: field.value, value: field.value }
                                  : null,
                                onChange: (val) => {
                                  if (val) {
                                    field.onChange(val.label);
                                    fetchCoordinates(val.value.place_id);
                                  } else {
                                    field.onChange("");
                                    setCoordinates({ lat: null, lng: null });
                                  }
                                },
                                placeholder:
                                  "Search for a place or use current location",
                                styles: {
                                  control: (provided) => ({
                                    ...provided,
                                    border: fieldState.error
                                      ? "1px solid red"
                                      : "1px solid rgba(0, 0, 0, 0.23)",
                                    boxShadow: "none",
                                    borderRadius: "4px",
                                    minHeight: "56px",
                                    width: "100%",
                                  }),
                                  menu: (provided) => ({
                                    ...provided,
                                    zIndex: 9999,
                                  }),
                                },
                              }}
                            />
                            {fieldState.error && (
                              <span
                                style={{ color: "red", fontSize: "0.75rem" }}
                              >
                                {fieldState.error.message}
                              </span>
                            )}
                          </div>
                        )}
                      />
                    )}

                    <Button
                      startIcon={<MyLocationIcon />}
                      onClick={getCurrentLocation}
                      disabled={isLoadingLocation}
                      sx={{
                        mt: 1,
                        textTransform: "none",
                        color: "#bb0000",
                        "&:hover": {
                          background: "rgba(187,0,0,0.05)",
                        },
                      }}
                    >
                      {isLoadingLocation
                        ? "Getting location..."
                        : "Use Current Location"}
                    </Button>

                    {locationError && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 1, display: "block" }}
                      >
                        {locationError}
                      </Typography>
                    )}

                    {coordinates.lat && coordinates.lng && (
                      <Typography
                        variant="caption"
                        sx={{ mt: 1, display: "block", color: "success.main" }}
                      >
                        Location set: {coordinates.lat.toFixed(6)},{" "}
                        {coordinates.lng.toFixed(6)}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>

              <Grid item xs={12} sx={{ mt: 2 }}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  onClick={toggleBirthDetails}
                  sx={{
                    cursor: "pointer",
                    padding: theme.spacing(1),
                    background: "#bb0000",
                    color: "#ffffff",
                    borderRadius: theme.spacing(1),
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", color: "inherit", flex: 1 }}
                  >
                    Additional Details
                  </Typography>
                  <Box display="flex" alignItems="center" ml={2}>
                    {isBirthDetailsExpanded ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </Box>
                </Box>

                <Collapse
                  in={isBirthDetailsExpanded}
                  timeout="auto"
                  unmountOnExit
                  sx={{ marginTop: 3 }}
                >
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Grid container spacing={{ xs: 2, sm: 3 }}>
                      <Grid item xs={12} sm={6}>
                        <Controller
                          name="dob"
                          control={control}
                          render={({ field }) => (
                            <DatePicker
                              {...field}
                              label="Date of Birth"
                              value={field.value ? parseISO(field.value) : null}
                              onChange={(newValue) => {
                                field.onChange(
                                  newValue
                                    ? newValue.toISOString().split("T")[0]
                                    : null
                                );
                              }}
                              slots={{
                                textField: TextField,
                              }}
                              slotProps={{
                                textField: {
                                  variant: "outlined",
                                  fullWidth: true,
                                },
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Controller
                          name="dot"
                          control={control}
                          defaultValue=""
                          render={({ field, fieldState }) => (
                            <TimePicker
                              {...field}
                              label="Select Time"
                              value={
                                field.value
                                  ? new Date(`1970-01-01T${field.value}`)
                                  : null
                              }
                              onChange={(newValue) => {
                                if (newValue) {
                                  const hours = String(
                                    newValue.getHours()
                                  ).padStart(2, "0");
                                  const minutes = String(
                                    newValue.getMinutes()
                                  ).padStart(2, "0");
                                  const seconds = "00";
                                  const formattedTime = `${hours}:${minutes}:${seconds}`;
                                  field.onChange(formattedTime);
                                } else {
                                  field.onChange(null);
                                }
                              }}
                              slots={{
                                textField: TextField,
                              }}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  variant: "outlined",
                                  error: !!fieldState.error,
                                  helperText: fieldState.error?.message,
                                },
                              }}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                    <Grid item xs={12} sx={{ mt: 1 }}>
                      <FormInputText
                        name="birthPlace"
                        control={control}
                        label="Birth Place"
                        placeholder="Enter birth place"
                      />
                    </Grid>
                  </LocalizationProvider>
                </Collapse>
              </Grid>

              <DialogActions
                sx={{
                  mt: 3,
                  p: 0,
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  onClick={onClose}
                  color="secondary"
                  disabled={isLoading}
                  sx={{
                    textTransform: "none",
                    mr: 2,
                    color: "#bb0000",
                    "&:hover": {
                      background: "rgba(187,0,0,0.05)",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isLoading || (!coordinates.lat && !coordinates.lng)}
                  startIcon={<SaveIcon />}
                  sx={{
                    textTransform: "none",
                    background: "#bb0000",
                    borderRadius: theme.spacing(2),
                    boxShadow: "0 4px 15px rgba(187,0,0,0.3)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: "#990000",
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 20px rgba(187,0,0,0.4)",
                    },
                  }}
                >
                  {isLoading ? "Submitting..." : "Create Property"}
                </Button>
              </DialogActions>
            </form>
          </Paper>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreatePropertyDialog;
